/**
 * Auth-failure handling for payment flows (PP-3820).
 *
 * A steady stream of client-portal payment requests reach anduin without a valid JWT/cookie and come
 * back **HTTP 401** — the `AuthInterceptor` only attaches the `Authorization` header when the token
 * loadable is LOADED, so on long-running / guest / deep-link sessions where the token has expired (or
 * hasn't re-loaded) by the time the user acts, the request goes out unauthenticated.
 *
 * A 401 is an authentication failure — NOT a card decline, NOT a payment-method validation error, and
 * NOT an authorization (403) failure — so it must never be rendered through decline/validation copy
 * (e.g. "Your card has been declined") or, worse, leak the raw `HttpErrorResponse.message`
 * ("Http failure response for <url>: 401 OK"). Every payment surface that turns an error into
 * user-facing text should short-circuit on {@link isAuthFailure} and show a session-expired message.
 */

/** HTTP status anduin returns when a payment request arrives without a valid token. */
const UNAUTHORIZED_STATUS = 401;

/**
 * Generic session-expired copy for inline error surfaces (e.g. the new-payment-method dialogs), which
 * render a message but have no dedicated reload control — so the copy itself asks the user to reload.
 */
export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please reload the page and try again.';

/**
 * Charge-specific variant used by the charge-failure dialog (`PaymentResultsDialog`), which pairs it
 * with a "Reload page" button and reassures the user that the payment did not go through.
 */
export const SESSION_EXPIRED_CHARGE_MESSAGE =
  'Your session has expired, so we couldn’t process your payment. Please reload the page to continue — no charge was made.';

/**
 * Whether an error is an authentication failure (a 401), as opposed to a genuine decline / validation
 * error. Reads the numeric HTTP `status` — present on the raw `HttpErrorResponse` surfaced by the
 * payment-method flows and on the marshaled `ChargePaymentError` from the charge flow. Accepts
 * `unknown` so every call site (typed error, `HttpErrorResponse`, marshaled shapes) can pass its error
 * straight through without a cast.
 */
export function isAuthFailure(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { status?: unknown }).status === UNAUTHORIZED_STATUS
  );
}
